/**
 * Utility functions for calculating pricing based on pricing rules
 */

import type { PricingRule } from '../graphql/types.graphql';

export type PricingRuleForSelect = Pick<
  PricingRule,
  'currency' | 'id' | 'price' | 'region' | 'subscriptionPeriod'
>;

export type PricingMatchTypeForSelect = {
  selectedRule: PricingRuleForSelect | null;
  matchType: 'global' | 'globalFallback' | 'noMatch' | 'region' | 'regionFallBack';
};

/**
 * Resolve the base-price rule for a (currency, period, region) selection.
 *
 * Currency is a HARD dimension: the backend prices a subscription strictly in
 * its locked currency (see `PricingRuleService.findActivePricingForTier`), so a
 * rule in a *different* currency is NOT an applicable price — billing it would
 * silently invoice 0 (there is no FX conversion in base-price resolution). This
 * resolver therefore only matches rules in the requested currency: a
 * region-specific rule first, then the region-agnostic (global) fallback. When
 * no same-currency rule exists it returns `noMatch` so the caller can flag the
 * plan as unpriced in this currency and block creation — rather than presenting
 * a foreign-currency amount the invoice will bill as 0.
 *
 * Within the currency constraint, precedence mirrors the backend
 * (`findActivePricingForTier`): region is the dominant axis and the exact term
 * beats a period-agnostic (unset `subscriptionPeriod`) default —
 *   region+exact term → region+any term → global+exact term → global+any term.
 * A rule authored without a term is a valid "default" the backend resolves for
 * every period, so the wizard must honor it too rather than showing the plan as
 * unpriced.
 *
 * The `regionFallBack` / `globalFallback` match types are retained on the union
 * for compatibility but are intentionally never produced: they described a
 * cross-currency fallback that the billing backend does not honor.
 */
export function findBestPricingRule(
  pricingRules: PricingRuleForSelect[],
  currency: string,
  period: string,
  regionId: string,
): PricingMatchTypeForSelect {
  if (pricingRules.length === 0) return { selectedRule: null, matchType: 'noMatch' };

  // Currency is a hard dimension — only rules in the requested currency are
  // billable, so everything below is chosen from this pool.
  const inCurrency = pricingRules.filter((rule) => rule.currency === currency);
  if (inCurrency.length === 0) return { selectedRule: null, matchType: 'noMatch' };

  const isRegion = (rule: PricingRuleForSelect) => rule.region?.id === regionId;
  const isGlobal = (rule: PricingRuleForSelect) => !rule.region;
  const exactTerm = (rule: PricingRuleForSelect) => rule.subscriptionPeriod === period;
  // A rule with no subscriptionPeriod applies to every term (period-agnostic).
  const anyTerm = (rule: PricingRuleForSelect) => rule.subscriptionPeriod == null;

  // Region-specific, most specific term first.
  const regionExact = inCurrency.find((r) => isRegion(r) && exactTerm(r));
  if (regionExact) return { selectedRule: regionExact, matchType: 'region' };
  const regionAny = inCurrency.find((r) => isRegion(r) && anyTerm(r));
  if (regionAny) return { selectedRule: regionAny, matchType: 'region' };

  // Global (region-agnostic) fallback, again exact term before any term.
  const globalExact = inCurrency.find((r) => isGlobal(r) && exactTerm(r));
  if (globalExact) return { selectedRule: globalExact, matchType: 'global' };
  const globalAny = inCurrency.find((r) => isGlobal(r) && anyTerm(r));
  if (globalAny) return { selectedRule: globalAny, matchType: 'global' };

  // A rule exists in this currency but not for this region/term combination —
  // treat as unpriced. We deliberately do NOT fall back to another currency:
  // the backend cannot bill it, so surfacing it would invoice 0.
  return { selectedRule: null, matchType: 'noMatch' };
}

export interface PricingContext {
  regionId: string;
  period: string;
  bundleId?: string;
  moduleId?: string;
}

/**
 * Format currency for display with currency symbol
 * Shows decimals only when they are not .00
 */
export function formatCurrency(amount: number, currency = 'USD'): string {
  const hasDecimals = amount % 1 !== 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Fraction digits a per-unit rate widens to before the fallback takes over. */
const MAX_RATE_FRACTION_DIGITS = 6;
/** Significant digits kept for a rate finer than MAX_RATE_FRACTION_DIGITS. */
const RATE_FALLBACK_SIGNIFICANT_DIGITS = 2;

/**
 * Format a per-unit RATE (an invoice line's unit price), which — unlike a
 * charge — may legitimately be sub-cent for metered pricing (per-email,
 * per-API-call). `formatCurrency` caps at the currency's minor unit, so a
 * $0.001 rate rendered as "$0.00" next to a non-zero amount; widen to the
 * precision the rate actually carries, up to six decimals, then to whatever
 * two significant digits need below that.
 *
 * Charges and totals keep `formatCurrency` so every amount column still shows
 * what is billed. Mirrors `rateFractionDigits` in the backend's money.util —
 * keep the two in step, INCLUDING the fallback: without it the dashboard showed
 * $0.00 for a rate the PDF and the line description rendered in full, which is
 * the same contradiction one surface further along.
 *
 * The one value where the two disagree is a rate sitting exactly on the
 * six-decimal tie (0.0000005): `toFixed` rounds ties to even so this renders the
 * exact rate, while the backend's decimal.js rounds half-up to "0.000001".
 * Both are truthful and differ by a millionth of a cent.
 */
export function formatUnitPrice(amount: number, currency = 'USD'): string {
  // Round to the cap first so float drift reports the digits the rate means,
  // then count what survives: 0.001 → 3, 10 → 0, 0.3000000000004 → 1.
  const rounded = Number(amount.toFixed(MAX_RATE_FRACTION_DIGITS));
  if (rounded === 0 && amount !== 0) {
    // Finer than the cap ($0.00000012/call). Significant-digit mode has no
    // ceiling to collide with, so the rate still renders as itself.
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumSignificantDigits: RATE_FALLBACK_SIGNIFICANT_DIGITS,
    }).format(amount);
  }
  const fractionDigits = (rounded.toString().split('.')[1] ?? '').length;
  const digits = Math.min(Math.max(fractionDigits, 2), MAX_RATE_FRACTION_DIGITS);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);
}

/**
 * Format currency in compact form (K, M) for display with currency symbol
 * Shows decimals only when they are not .00
 * Used for displaying large values like ARR/MRR in dashboards
 */
export function formatCompactCurrency(amount: number, currency = 'USD'): string {
  const symbol =
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value || '$';

  if (amount >= 1000000) {
    const value = amount / 1000000;
    const formatted = value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
    return `${symbol}${formatted}M`;
  }
  if (amount >= 1000) {
    const value = amount / 1000;
    const formatted = value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
    return `${symbol}${formatted}K`;
  }
  return formatCurrency(amount, currency);
}
